/**
 * Org context — resolving which organisation the user is acting in.
 *
 * **This module exists because the app shipped without it.** `setOrgId` had no
 * caller: nothing ever fetched the user's organisations, so `getOrgId()` was
 * always null and every signed-in user landed on "Choose an organisation" —
 * with no switcher to choose from, because `<PlatformShell>`'s `org` prop was
 * never passed either. A dead end for everybody, found by running it
 * (2026-08-25).
 *
 * The precedence below is copy-adapted from `apps/store`'s, which in turn came
 * from `apps/launcher` after its missing-header 400s. The rule it encodes:
 * **resolving and remembering are one act.** Resolve without persisting and
 * the next page load resolves differently; persist without checking membership
 * and a user removed from an org is stuck on it forever.
 */

import { ORGS_API } from './config'
import { session } from './session'

export interface OrgRow {
  id: string
  display_name: string
  role: string
  is_default_org?: boolean
}

/**
 * Our own key. Per-app because storage is per-origin, and because two apps
 * remembering "the current org" for each other is how one app's switch
 * silently moves another.
 */
export const SELECTED_ORG_KEY = 'fiveox.gst_org'

export async function fetchOrgs(): Promise<OrgRow[]> {
  if (!session.getSession()) return []
  try {
    // `session.authed` owns the bearer and the single serialized refresh, so a
    // token that expired while this page was open recovers here rather than
    // rendering an org-less app.
    const res = await session.authed(ORGS_API)
    if (!res.ok) return []
    const body = (await res.json()) as { data?: OrgRow[] }
    return body.data ?? []
  } catch {
    // Org-service unreachable. Not a dead session, and not a reason to sign
    // anyone out — the caller renders "we can't reach your organisations".
    return []
  }
}

export function getSelectedOrgId(): string | null {
  try {
    return localStorage.getItem(SELECTED_ORG_KEY)
  } catch {
    return null
  }
}

export function setSelectedOrgId(orgId: string | null): void {
  try {
    if (orgId) localStorage.setItem(SELECTED_ORG_KEY, orgId)
    else localStorage.removeItem(SELECTED_ORG_KEY)
  } catch {
    /* private mode; the in-memory selection still works for this tab */
  }
}

/**
 * Which org opens first.
 *
 * 1. The one previously chosen — **but only if the user is still a member**.
 *    Skipping that check leaves someone removed from an org permanently stuck
 *    on it, because every request 404s and nothing offers a way out.
 * 2. Otherwise the user's default org.
 * 3. Otherwise the first membership.
 *
 * Returns null only when the user genuinely belongs to no organisation.
 */
export function resolveSelected(orgs: OrgRow[]): OrgRow | null {
  if (orgs.length === 0) return null
  const remembered = getSelectedOrgId()
  const stillAMember = remembered ? orgs.find((o) => o.id === remembered) : undefined
  return stillAMember ?? orgs.find((o) => o.is_default_org) ?? orgs[0] ?? null
}
